import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Paperclip, Download, Trash2, FilePlus2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const MAX_ATTACH_TOTAL = 50 * 1024 * 1024;
export const fmtSize = (b) => (b === 0 ? "0 KB" : b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const CATEGORY_META = {
  rules: { label: "Rules & Procedures", cls: "bg-[#E6F0F9] text-[#0050A0] border-[#0050A0]/30" },
  revision: { label: "Revision", cls: "bg-amber-50 text-amber-700 border-amber-300" },
  reference: { label: "Reference", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

export default function AttachmentsPanel({ tender, canManage }) {
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const categoryRef = useRef("reference");

  const load = useCallback(() => {
    axios.get(`${API}/tenders/${tender.id}/attachments`).then((r) => setAttachments(r.data));
  }, [tender.id]);
  useEffect(() => { load(); }, [load]);

  const totalUsed = attachments.reduce((s, a) => s + a.size, 0);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const newTotal = totalUsed + files.reduce((s, f) => s + f.size, 0);
    if (newTotal > MAX_ATTACH_TOTAL) {
      toast.error(`Total attachments limited to 50 MB per tender (${fmtSize(totalUsed)} already used).`);
      e.target.value = "";
      return;
    }
    setUploading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", categoryRef.current);
      try {
        await axios.post(`${API}/tenders/${tender.id}/attachments`, fd);
      } catch (err) {
        toast.error(`${f.name}: ${err.response?.data?.detail || "upload failed"}`);
      }
    }
    setUploading(false);
    e.target.value = "";
    load();
  };

  const remove = async (id) => {
    await axios.delete(`${API}/attachments/${id}`);
    toast.info("Attachment removed.");
    load();
  };

  if (!attachments.length && !canManage) return null;

  return (
    <div className="bg-white border border-slate-200 p-6 mb-8" data-testid="attachments-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-[#0050A0]" aria-hidden="true" />
          <h3 className="font-serif text-lg text-slate-900">Reference Documents</h3>
          <span className="font-mono text-xs text-slate-400" data-testid="attachments-total-size">{fmtSize(totalUsed)} / 50 MB</span>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <input ref={inputRef} type="file" multiple accept=".pdf,.docx,.txt,.md,.csv" className="hidden" onChange={upload} data-testid="attachment-file-input" />
            <button
              onClick={() => { categoryRef.current = "reference"; inputRef.current?.click(); }}
              disabled={uploading}
              data-testid="add-attachment-btn"
              className="inline-flex items-center gap-2 border border-slate-300 text-slate-600 px-4 py-2 text-xs font-semibold hover:bg-slate-900 hover:text-white transition-colors duration-200 disabled:opacity-60"
            >
              <FilePlus2 className="w-3.5 h-3.5" aria-hidden="true" /> {uploading ? "Uploading…" : "Attach Reference"}
            </button>
            <button
              onClick={() => { categoryRef.current = "rules"; inputRef.current?.click(); }}
              disabled={uploading}
              data-testid="add-rules-attachment-btn"
              className="inline-flex items-center gap-2 bg-[#0050A0] text-white px-4 py-2 text-xs font-semibold hover:bg-[#003F7A] transition-colors duration-200 disabled:opacity-60"
            >
              <FilePlus2 className="w-3.5 h-3.5" aria-hidden="true" /> Attach Rules & Procedures
            </button>
          </div>
        )}
      </div>
      {attachments.length === 0 ? (
        <p className="text-xs text-slate-400" data-testid="attachments-empty">
          No reference documents attached. Attached material (PDF, DOCX, TXT, MD, CSV) is read by the AI when drafting the tender document.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {attachments.map((a, i) => (
            <li key={a.id} className="flex items-center justify-between py-2.5 gap-4" data-testid={`attachment-row-${i}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <p className="text-sm text-slate-800 truncate">{a.filename}</p>
                  <span className={`text-[10px] px-1.5 py-0.5 border shrink-0 uppercase tracking-wide ${(CATEGORY_META[a.category] || CATEGORY_META.reference).cls}`} data-testid={`attachment-category-${i}`}>
                    {(CATEGORY_META[a.category] || CATEGORY_META.reference).label}
                  </span>
                </div>
                <p className="text-[11px] text-slate-400 font-mono">{fmtSize(a.size)} · {a.extracted_chars > 0 ? "text extracted for AI drafting" : "no extractable text"}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a href={`${API}/attachments/${a.id}/download`} data-testid={`download-attachment-${i}`} className="p-2 text-slate-500 hover:text-[#0050A0] transition-colors duration-200" title="Download">
                  <Download className="w-4 h-4" aria-hidden="true" />
                </a>
                {canManage && (
                  <button onClick={() => remove(a.id)} data-testid={`delete-attachment-${i}`} className="p-2 text-slate-400 hover:text-red-600 transition-colors duration-200" title="Remove">
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
